import { lazy, Suspense, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/auth/session";
import { useGate, type Gate } from "@/components/site/gateContext";
import CheckPage from "@/components/tools/CheckPage";
import PostingUnread from "@/components/tools/PostingUnread";
import TailorStatus from "@/components/tools/TailorStatus";
import UploadGuard from "@/components/tools/UploadGuard";
import type { Posting } from "@/lib/api/catalogue";
import { usePosting } from "@/lib/hooks/catalogue";
import { useResumeCheck } from "@/lib/hooks/resumeCheck";
import { useToolTailor, type ToolTailor } from "@/lib/hooks/toolTailor";
import { isUuid } from "@/lib/ids";
import { pageTitle, usePageTitle } from "@/lib/pageTitle";
import { checkPageName } from "@/lib/posting/page";
import { RESUME_CHECK_COPY } from "@/lib/tools/copy";
import { usePastedHandoff } from "@/lib/tools/handoff";
import { isPostingJob, type ToolJob } from "@/lib/tools/pastedJob";
import { postingTailorJob } from "@/lib/tools/postingJob";
import { UPLOAD_UNLOADED } from "@/lib/tools/tailorAnswer";

const UploadDialog = lazy(() => import("@/components/resume/UploadDialog"));

/** A press once the sign-in is read: to the tailor for a member, to the sheet for a visitor. */
function goOn(
    job: ToolJob,
    member: boolean,
    { ask, run, posting }: { ask: Gate["ask"]; run: ToolTailor; posting: Posting | null },
): void {
    const postingId = isPostingJob(job) ? job.postingId : undefined;
    if (!member) ask("tool_tailor", postingId, job);
    else if (!isPostingJob(job)) run.start(job);
    else if (posting) run.start(postingTailorJob(posting, job.resume));
}

/**
 * The free resume keyword checker, at `/tools/resume-keyword-checker` and,
 * on one posting's keywords, under `:postingId`. An id that is not one the
 * catalogue gives asks nothing of anyone and shows the plain tool.
 *
 * Tailor is a signed-out person's way to an account. Back from it, the
 * job they had is handed over once, the boxes are put back and the tailor
 * starts, as it does at once for a member's press. A posting's job is read
 * again first: it can have closed while they signed up, and then nothing
 * offers to tailor for it.
 */
export default function ResumeCheck() {
    const { postingId: given } = useParams();
    const postingId = isUuid(given) ? given : null;
    const posting = usePosting(postingId);
    const found = posting.data ?? null;
    // A row can lack its keywords: that is a posting that names none, never a pasted job.
    const tool = useResumeCheck(found ? (found.keywords ?? []) : null);
    const { ask } = useGate();
    const { state } = useAuth();
    const member = state.status === "signed-in";
    const reading = state.status === "loading";
    const run = useToolTailor();
    const handed = usePastedHandoff();
    const restored = useRef<ToolJob | null>(null);
    // A press before the sign-in is read: a member must not meet the sign-up sheet.
    const held = useRef<ToolJob | null>(null);
    usePageTitle(found ? pageTitle(checkPageName(found)) : RESUME_CHECK_COPY.title);

    useEffect(() => {
        if (!handed || restored.current === handed) return;
        if (!isPostingJob(handed)) {
            restored.current = handed;
            tool.restore(handed.description, handed.resume);
            run.start(handed);
            return;
        }
        if (!found) return;
        restored.current = handed;
        if (handed.postingId !== found.posting_id) return;
        tool.restore("", handed.resume);
        if (!found.closed_at) run.start(postingTailorJob(found, handed.resume));
    }, [handed, found, tool, run]);

    useEffect(() => {
        if (reading || !held.current) return;
        const job = held.current;
        held.current = null;
        goOn(job, member, { ask, run, posting: found });
    }, [reading, member, ask, run, found]);

    if (postingId && !found) return <PostingUnread error={posting.error} />;
    return (
        <>
            <CheckPage
                tool={tool}
                posting={found}
                visitor={state.status === "signed-out"}
                member={member}
                onTailor={(job) => {
                    if (reading) held.current = job;
                    else goOn(job, member, { ask, run, posting: found });
                }}
                tailoring={run.step === "tailoring" || run.step === "upload"}
                tailored={run.step === "ready"}
                status={
                    <TailorStatus
                        step={run.step}
                        stage={run.stage}
                        line={run.line}
                        resumeId={run.resumeId}
                    />
                }
                keptJob={run.keptJob}
            />
            {run.step === "upload" && (
                <UploadGuard onFailed={() => run.closed(UPLOAD_UNLOADED)}>
                    <Suspense fallback={null}>
                        <UploadDialog
                            open
                            baseline={null}
                            text={tool.resume}
                            onSaved={run.saved}
                            onClose={() => run.closed()}
                        />
                    </Suspense>
                </UploadGuard>
            )}
        </>
    );
}
